/** Public SEO/AEO exam guide pages - /guides and /guides/:slug */
import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { SUBJECT_GUIDES } from "./guidesSubjectData";
import guideSeo from "@/seo/guideSeo";

const GUIDE_SEO = guideSeo.GUIDE_SEO;
const SITE = "https://passadc.com";
const DEFAULT_PUBLISHED = "2025-10-01";
const DEFAULT_MODIFIED = "2026-02-15";

function setMeta(selector, attr, value, cache) {
  const el = document.querySelector(selector);
  if (!el) return;
  cache[selector] = el.getAttribute(attr);
  el.setAttribute(attr, value);
}
function restoreMeta(selector, attr, cache) {
  const el = document.querySelector(selector);
  if (el && cache[selector] != null) el.setAttribute(attr, cache[selector]);
}

function useGuideSeo(slug, title, description, opts = {}) {
  const { faqs, published = DEFAULT_PUBLISHED, modified = DEFAULT_MODIFIED } = opts;
  useEffect(() => {
    const cache = {};
    document.title = title;
    setMeta('meta[name="description"]', "content", description, cache);
    const canonicalUrl = slug ? `${SITE}/guides/${slug}` : `${SITE}/guides`;
    setMeta('link[rel="canonical"]', "href", canonicalUrl, cache);
    setMeta('meta[property="og:title"]', "content", title.replace(" | PassADC", ""), cache);
    setMeta('meta[property="og:description"]', "content", description, cache);
    setMeta('meta[property="og:url"]', "content", canonicalUrl, cache);
    setMeta('meta[name="twitter:title"]', "content", title.replace(" | PassADC", ""), cache);
    setMeta('meta[name="twitter:description"]', "content", description, cache);

    const scripts = [];
    if (slug) {
      const article = document.createElement("script");
      article.type = "application/ld+json";
      article.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: title.replace(" | PassADC", ""),
        description,
        url: canonicalUrl,
        datePublished: published,
        dateModified: modified,
        author: { "@type": "Organization", name: "PassADC", url: SITE },
        publisher: {
          "@type": "Organization",
          name: "PassADC",
          url: SITE,
          logo: { "@type": "ImageObject", url: `${SITE}/apple-touch-icon.png` },
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
      });
      document.head.appendChild(article);
      scripts.push(article);

      const crumbs = document.createElement("script");
      crumbs.type = "application/ld+json";
      crumbs.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "PassADC", item: SITE },
          { "@type": "ListItem", position: 2, name: "ADC Part 1 Guides", item: `${SITE}/guides` },
          { "@type": "ListItem", position: 3, name: title.replace(" | PassADC", ""), item: canonicalUrl },
        ],
      });
      document.head.appendChild(crumbs);
      scripts.push(crumbs);

      if (faqs && faqs.length) {
        const faq = document.createElement("script");
        faq.type = "application/ld+json";
        faq.textContent = JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map(([q, a]) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        });
        document.head.appendChild(faq);
        scripts.push(faq);
      }
    }
    window.scrollTo(0, 0);
    return () => {
      document.title = "PassADC - The smartest way to prepare for the Australian Dental Council exam";
      restoreMeta('meta[name="description"]', "content", cache);
      restoreMeta('link[rel="canonical"]', "href", cache);
      restoreMeta('meta[property="og:title"]', "content", cache);
      restoreMeta('meta[property="og:description"]', "content", cache);
      restoreMeta('meta[property="og:url"]', "content", cache);
      restoreMeta('meta[name="twitter:title"]', "content", cache);
      restoreMeta('meta[name="twitter:description"]', "content", cache);
      scripts.forEach((s) => s.remove());
    };
  }, [slug, title, description, faqs, published, modified]);
}

// Official ADC Written Examination blueprint — ADC Written Examination Handbook for
// General Dentistry, Rev E (Sept 2025). Mirrors /api/public/adc-blueprint.
const BLUEPRINT = [
  ["Restorative dentistry (incl. tooth surface loss)", "14%"],
  ["Paediatric dentistry and orthodontics", "10%"],
  ["Preventive dentistry & behaviour modification", "10%"],
  ["Periodontics", "9%"],
  ["Dental emergencies", "8%"],
  ["General medicine (incl. medical emergencies, special needs)", "8%"],
  ["Endodontics", "7%"],
  ["Oral medicine & pathology", "7%"],
  ["Oral surgery", "7%"],
  ["Fixed prosthodontics", "5%"],
  ["Infection prevention & control", "5%"],
  ["Pain & anxiety management", "5%"],
  ["Removable prosthodontics", "5%"],
];
const SUBDISCIPLINES = [["Pharmacology", "10%"], ["Radiology", "10%"], ["Implantology", "4%"]];

const CTA = ({ label = "Take the free readiness check" }) => {
  const navigate = useNavigate();
  return (
    <div className="my-10 rounded-2xl border border-primary/25 bg-primary/5 p-6 text-center not-prose">
      <p className="font-semibold mb-1">Where do you actually stand?</p>
      <p className="text-sm text-muted-foreground mb-4">30 questions weighted to the official ADC blueprint. Free, instant, no credit card.</p>
      <Button className="rounded-full px-6" onClick={() => navigate("/diagnostic")} data-testid="guide-cta-btn">
        {label} <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

// STEP 31 — pass-rate guide "answer box" + primary CTA, rendered under the H1.
const PassRateAnswerBox = () => {
  const navigate = useNavigate();
  return (
    <>
      <div className="my-6 rounded-2xl border border-primary/30 bg-muted/40 p-5 not-prose" data-testid="passrate-answer-box">
        <p className="font-semibold mb-2">ADC exam pass rates at a glance (official ADC Annual Report 2024-25)</p>
        <ul className="text-sm space-y-1 list-disc pl-5">
          <li><strong>Written exam (Part 1):</strong> 12% in 2024-25 (404 of 3,325 candidates). It was 41% in 2023-24.</li>
          <li><strong>Practical exam (Part 2):</strong> 11% in 2024-25 (205 of 1,880), down from 15%.</li>
          <li>These are all-candidate figures. The ADC does not publish first-attempt or resit pass rates.</li>
          <li>Earlier written-exam rates: 50% (2019-20), 44% (2020-21), 24% (2021-22), 24% (2022-23).</li>
        </ul>
        <p className="text-xs text-muted-foreground mt-2">Source: <a href="https://www.adc.org.au/files/corporate/annual-reports/ADC_Annual_Report_2024_2025.pdf" target="_blank" rel="noopener noreferrer" className="underline">ADC Annual Report 2024-25</a></p>
      </div>
      <div className="my-6 rounded-2xl border border-primary/25 bg-primary/5 p-6 not-prose">
        <p className="font-semibold mb-1">See where you stand before you pay $2,122.</p>
        <p className="text-sm text-muted-foreground mb-4">Try 10 free ADC-style questions with full explanations and find your weakest discipline.</p>
        <Button className="rounded-full px-6" onClick={() => navigate("/diagnostic")} data-testid="passrate-cta-btn">
          Try 10 free questions <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </>
  );
};


export const GUIDES = {
  "what-is-the-adc-part-1": {
    title: "What is the ADC Part 1 Exam? Format, Pass Rate, Fees & Subjects (2026 Guide)",
    description: "ADC Part 1 written exam explained for overseas-trained dentists: 280 questions over 2 days, official 12% pass rate (2024-25), AUD $2,122 fee, the 13-discipline blueprint - and how to prepare in 2026.",
    faqs: [
      ["What is the ADC Part 1 exam?", "The ADC Part 1 is the written examination overseas-trained dentists must pass on the Australian Dental Council pathway. It is a computer-based, 280-question, single-best-answer MCQ exam delivered over two consecutive days at Pearson VUE test centres."],
      ["How many questions are in the ADC Part 1 written exam?", "280 multiple-choice questions total, split into 4 sections of 70 questions. Two sections per day, 2 hours per section. Questions are scenario-based, with roughly 56 clinical vignettes across the exam."],
      ["What is the ADC Part 1 pass rate?", "The ADC Annual Report 2024-25 reports an overall written-examination pass rate of 12% for 2024-25 (404 of 3,325 candidates), down from 41% in 2023-24 (1,209 of 2,925). These are all-candidate figures, not first-attempt rates - the ADC does not publish first-attempt data or a cause for the decline. Structured preparation targeted at the highest-weight disciplines matters more than ever."],
      ["How much does the ADC Part 1 exam cost?", "The written examination fee is AUD $2,122 per attempt (adc.org.au, 2026). A separate initial assessment of your qualifications is also required. See our full ADC Part 1 cost breakdown."],
      ["What subjects are on the ADC Part 1?", "The official blueprint has 13 disciplines: Restorative dentistry 14%, Paediatric dentistry & orthodontics 10%, Preventive dentistry & behaviour modification 10%, Periodontics 9%, Dental emergencies 8%, General medicine 8%, Endodontics 7%, Oral medicine & pathology 7%, Oral surgery 7%, Fixed prosthodontics 5%, Infection prevention & control 5%, Pain & anxiety management 5%, Removable prosthodontics 5%. Pharmacology (10%), radiology (10%) and implantology (4%) are assessed across disciplines."],
      ["How long should I study for the ADC Part 1?", "Most working dentists need 8–12 weeks of focused study at 1.5–2 hours a day, ideally structured around diagnosing weak subjects first, then rebuilding them with scenario-based MCQs and full-length timed simulations."],
      ["How many attempts do I get at the ADC Part 1?", "There is no hard cap on attempts, but each attempt costs another full exam fee, so first-attempt success is what everyone is really optimising for."],
      ["Can I sit the ADC Part 1 in my home country?", "Yes - the exam is delivered at Pearson VUE test centres worldwide, so you don't need to travel to Australia for the written exam."],
      ["What comes after ADC Part 1?", "Passing the written exam qualifies you to apply for the ADC Part 2 practical examination, which is the clinical assessment stage of the pathway."],
    ],
    body: (
      <>
        <p>
          The <strong>ADC Part 1 exam</strong> - officially the <strong>ADC written examination</strong> - is the
          first assessment overseas-trained dentists must pass on the Australian Dental Council pathway to
          registering and practising as a dentist in Australia. It tests the scientific and clinical knowledge
          expected of a newly graduated Australian dentist, and it's the gateway to the rest of the pathway.
        </p>

        <h2>ADC Part 1 exam format at a glance</h2>
        <ul>
          <li><strong>280 multiple-choice questions</strong> in total, single best answer</li>
          <li>Delivered by computer over <strong>two consecutive days</strong> (Pearson VUE test centres worldwide)</li>
          <li><strong>4 sections of 70 questions</strong> - two sections per day, <strong>2 hours per section</strong></li>
          <li>Questions are <strong>scenario-based</strong>: each clinical vignette carries around 5 related questions (roughly 56 scenarios across the exam)</li>
          <li>No penalty for wrong answers - never leave a question blank</li>
        </ul>
        <p>
          The next written exam is on 17–18 March 2027. Bookings for that sitting open 8–15 December 2026
          under the ADC's new booking process: you choose a Pearson location and pay in the same session,
          with a two-hour hold. You need a valid initial assessment first. See{" "}
          <a href="https://www.adc.org.au/assessments/dentists/written-examination/" target="_blank" rel="noopener noreferrer">ADC written examination</a>{" "}
          and the <a href="https://www.adc.org.au/news/changes-to-the-written-examination-booking-procedure/" target="_blank" rel="noopener noreferrer">booking change notice</a>.
          Read our guide: <Link to="/guides/book-adc-written-exam-seat">How to book your ADC written exam seat</Link>.
        </p>

        <h2>ADC Part 1 pass rate: why it's harder than most expect</h2>
        <p>
          The ADC's own Annual Report 2024-25 puts the overall written-exam pass rate at <strong>12%</strong> (404 of
          3,325 candidates), down from 41% the year before. The exam isn't tricky for the sake of it - it's just wide. Candidates who prep by re-reading
          textbooks or hoarding recalled questions tend to underperform on scenario-based MCQs. Those who
          diagnose their weakest high-weight subjects and drill scenario questions with explanations tend to
          pass first time.
        </p>

        <h2>ADC Part 1 exam fees (2026)</h2>
        <p>
          The written examination fee is <strong>AUD $2,122</strong> per attempt (adc.org.au, 2026) at the time of
          writing, on top of a separate <strong>initial assessment of qualifications</strong> fee. Always
          confirm the current fees on <a href="https://adc.org.au" target="_blank" rel="noreferrer">adc.org.au</a>.
          Because the fee applies per attempt, failing once effectively doubles the exam cost - see our
          full <Link to="/guides/adc-part-1-cost">ADC Part 1 cost breakdown</Link> for the complete picture
          including prep costs.
        </p>

        <h2>ADC Part 1 subjects and weighting</h2>
        <p>The official blueprint distributes the 280 questions across 13 disciplines (target % of questions):</p>
        <table>
          <thead><tr><th>Discipline</th><th>Target %</th></tr></thead>
          <tbody>
            {BLUEPRINT.map(([s, w]) => <tr key={s}><td>{s}</td><td>{w}</td></tr>)}
          </tbody>
        </table>
        <p>Three sub-disciplines are assessed <em>across</em> those disciplines rather than as separate blocks:</p>
        <table>
          <thead><tr><th>Sub-discipline</th><th>Target %</th></tr></thead>
          <tbody>
            {SUBDISCIPLINES.map(([s, w]) => <tr key={s}><td>{s}</td><td>{w}</td></tr>)}
          </tbody>
        </table>
        <p style={{ fontSize: "0.85em" }}>
          Source: <a href="https://www.adc.org.au/files/assessment/handbooks/ADC_Written_Examination_Handbook_for_General_Dentistry.pdf" target="_blank" rel="noopener noreferrer">ADC Written Examination Handbook for General Dentistry</a>, Rev E, September 2025. Questions are also mapped to four competency clusters (20% / 30% / 30% / 20%) and you must achieve grade A or B in <strong>every</strong> cluster to pass.
        </p>
        <p>
          This weighting matters more than most candidates realise: a candidate who is strong in endodontics
          but weak in restorative dentistry is leaving the biggest block of marks on the table. Smart preparation
          targets your weakest high-weight subjects first. Every subject has its own high-yield topics and
          traps - see the <Link to="/guides">subject-by-subject guides</Link>.
        </p>

        <h2>How should you prepare for the ADC Part 1?</h2>
        <ol>
          <li><strong>Diagnose your gaps first.</strong> Don't start on page one of a textbook - find out which subjects are actually costing you marks. A short <Link to="/diagnostic">readiness check</Link> across every discipline in the ADC blueprint is a much better starting point.</li>
          <li><strong>Practise scenario-based MCQs</strong> - the real exam is case-driven, so isolated fact recall isn't enough. You need to think through a vignette, then answer 5 linked questions on it.</li>
          <li><strong>Sit full-length timed simulations.</strong> 140 questions in a day is a stamina event; you need to have experienced it before exam day, not for the first time on exam day.</li>
          <li><strong>Follow a plan, not vibes.</strong> See our <Link to="/guides/adc-part-1-study-plan">8-week ADC Part 1 study plan</Link> for a working template.</li>
        </ol>

        <h2>Related ADC Part 1 guides</h2>
        <p>
          Deep-dive resources: <Link to="/guides/adc-part-1-pass-rate">ADC Part 1 pass rate explained</Link>{" "}
          · <Link to="/guides/how-to-pass-adc-part-1">how to pass ADC Part 1 first time</Link> ·{" "}
          <Link to="/guides/adc-part-1-cost">full cost breakdown</Link> ·{" "}
          <Link to="/guides/adc-part-1-study-plan">8-week study plan</Link>. Country-specific:{" "}
          <Link to="/guides/adc-exam-cost-india">India (₹)</Link> ·{" "}
          <Link to="/guides/adc-exam-cost-pakistan">Pakistan (PKR)</Link>.
        </p>
      </>
    ),
  },
  "adc-part-1-study-plan": {
    title: "An 8-Week ADC Part 1 Study Plan That Actually Works",
    description: "A week-by-week ADC written exam study plan for working dentists: diagnose weaknesses, master high-weight subjects, and rehearse full exam days.",
    body: (
      <>
        <p>
          Most ADC candidates study while working full-time. The plan below assumes <strong>1.5–2 focused
          hours a day</strong> and is built around one principle: <em>spend your hours where the marks are -
          your weakest high-weight subjects.</em>
        </p>

        <h2>Weeks 1: Diagnose, don't guess</h2>
        <p>
          Sit a balanced diagnostic across every discipline in the ADC blueprint and rank your accuracy per subject against the
          exam weighting. Your priority list = high weighting × low accuracy. Oral Surgery at 55% accuracy
          beats Community Dentistry at 40% - it carries far more of the marks.
        </p>

        <h2>Weeks 2–4: Rebuild your weakest three subjects</h2>
        <ul>
          <li>Work lesson-by-lesson with a <strong>pre-test → learn → post-test</strong> loop so you know each lesson actually stuck</li>
          <li>Finish every day with 20–30 mixed MCQs from those subjects</li>
          <li>Review every wrong answer's explanation the same day - the explanation is where the learning happens</li>
        </ul>

        <h2>Weeks 5–6: Broaden and interleave</h2>
        <p>
          Bring the remaining subjects in. Interleave - mix subjects within each practice session rather
          than doing single-subject days. Interleaving feels harder, and that's precisely why it works for
          a mixed-format exam. Start spaced-repetition reviews of everything you got wrong earlier.
        </p>

        <h2>Week 7: Full simulation</h2>
        <p>
          Sit a <strong>full-length, timed, blueprint-weighted mock</strong> (140 questions, 4 hours). Treat it
          like the real day: same start time, timed breaks, no pausing. Then spend two days reviewing every
          incorrect answer.
        </p>

        <h2>Week 8: Repair and rehearse</h2>
        <ul>
          <li>Target only the weak areas the mock exposed</li>
          <li>Sit a second full simulation 3–4 days before the exam</li>
          <li>Final 48 hours: review flagged questions and takeaways only - no new content</li>
        </ul>
      </>
    ),
  },
  "adc-part-1-cost": {
    title: "ADC Part 1 Exam Fees 2026: Full Cost Breakdown (AUD, USD, GBP, INR, PKR)",
    description: "How much does the ADC Part 1 written exam cost in 2026? Initial assessment fees, AUD $2,122 exam fee per attempt, prep options and total real cost - with currency conversions for overseas-trained dentists.",
    faqs: [
      ["How much does the ADC Part 1 exam cost in 2026?", "The ADC Part 1 written exam application fee is AUD $2,122 per attempt in 2026, on top of a separate initial assessment of qualifications fee of roughly AUD $647. Always confirm current fees on adc.org.au before applying."],
      ["What is the ADC initial assessment fee?", "The ADC initial assessment of overseas dental qualifications is a separate fee, typically several hundred Australian dollars, paid before you can apply to sit the written exam. It reviews your primary dental degree and eligibility."],
      ["How much is the ADC Part 1 exam fee per attempt?", "AUD $2,122 per attempt (adc.org.au, 2026). Because the fee applies per attempt, failing once effectively doubles your exam cost - which is why first-attempt preparation is so important."],
      ["What is the total cost to pass ADC Part 1?", "Most candidates pay the initial assessment ($647) and the written exam fee ($2,122), plus whatever they spend on preparation and any resit. Prep can range from free self-study to paid courses, so check what each one actually includes before you pay."],
      ["How much does ADC Part 1 cost in USD, GBP, INR and PKR?", "At current exchange rates, AUD $2,122 is approximately US$1,380, £1,090, ₹115,000, and Rs 385,000. Rates fluctuate daily - check xe.com on the day you pay the ADC in AUD."],
      ["Is the ADC Part 1 exam fee refundable?", "The ADC written exam application fee is generally non-refundable once you have applied and been allocated a testing window. Postponement policies exist but usually incur fees - always read the current ADC candidate handbook."],
      ["How can I reduce the cost of preparing for ADC Part 1?", "Free options include the PassADC free tier (lessons covering every ADC subject, 10 MCQs a day, 1 full Readiness Trial). Paid PassADC options are $19/month or the $89 March 2027 Pass (one-time, valid to 31 March 2027)."],
    ],
    body: (
      <>
        <p>
          The <strong>ADC Part 1 exam fees</strong> are a serious financial commitment, and the written exam
          is where most overseas-trained dentists first feel it. Here's the honest, 2026 breakdown of every
          ADC cost - application fees, exam fees, preparation costs and what the total really adds up to.
        </p>
        <p>
          For the full breakdown of official ADC fees, resit costs and how to judge prep, read{" "}
          <Link to="/guides/what-the-adc-exam-really-costs-2026">What the ADC exam really costs in 2026</Link>.
        </p>

        <h2>ADC Part 1 exam fees: the two unavoidable costs</h2>
        <table>
          <thead><tr><th>Fee</th><th>Approx. cost (AUD)</th><th>When you pay</th></tr></thead>
          <tbody>
            <tr><td><strong>Initial assessment of qualifications</strong></td><td>~$647</td><td>Before you can apply for the exam</td></tr>
            <tr><td><strong>ADC written exam application fee</strong></td><td>$2,122 per attempt</td><td>When you apply for a testing window</td></tr>
            <tr><td><strong>Practical exam (Part 2)</strong></td><td>$4,775 per attempt</td><td>After you pass the written exam</td></tr>
          </tbody>
        </table>
        <p>
          Both fees are set and periodically updated by the Australian Dental Council - always confirm the
          current amounts at <a href="https://adc.org.au" target="_blank" rel="noreferrer">adc.org.au</a>{" "}
          before you plan your budget. The exam fee applies <em>per attempt</em>, so failing once effectively
          doubles the ADC exam cost. In real terms, the cheapest ADC Part 1 exam is the one you only sit once.
        </p>

        <h2>ADC Part 2 (practical exam) fees</h2>
        <p>
          Practical examination application: AUD $4,775 per attempt. Application for review of a result:
          AUD $1,008. Independent appeal committee: AUD $5,305. Source:{" "}
          <a href="https://www.adc.org.au/assessments/additional-information/fees/" target="_blank" rel="noopener noreferrer">ADC fees</a>.
        </p>

        <h2>ADC Part 1 exam fees in USD, GBP, INR and PKR</h2>
        <p>Approximate conversions at recent exchange rates (check xe.com for live rates):</p>
        <table>
          <thead><tr><th>Currency</th><th>Initial assessment (~AUD $647)</th><th>Written exam fee (AUD $2,122)</th></tr></thead>
          <tbody>
            <tr><td>US Dollar (USD)</td><td>~$330</td><td>~$1,380</td></tr>
            <tr><td>Pound Sterling (GBP)</td><td>~£260</td><td>~£1,090</td></tr>
            <tr><td>Euro (EUR)</td><td>~€305</td><td>~€1,280</td></tr>
            <tr><td>Indian Rupee (INR)</td><td>~₹35,500</td><td>~₹115,000</td></tr>
            <tr><td>Pakistani Rupee (PKR)</td><td>~Rs 119,000</td><td>~Rs 385,000</td></tr>
            <tr><td>UAE Dirham (AED)</td><td>~AED 1,210</td><td>~AED 5,080</td></tr>
          </tbody>
        </table>
        <p className="text-sm text-muted-foreground">
          These are indicative conversions only. FX rates move daily, and the ADC bills in AUD - your bank's
          rate on payment day is what actually applies.
        </p>

        <h2>ADC Part 1 preparation cost: the optional cost everyone argues about</h2>
        <p>Roughly two tiers of ADC Part 1 preparation exist:</p>
        <table>
          <thead><tr><th>Prep option</th><th>Typical cost</th><th>Trade-off</th></tr></thead>
          <tbody>
            <tr>
              <td><strong>Pure self-study</strong></td>
              <td>~$0–$300</td>
              <td>Textbooks, scattered PDFs, recalled-question files. Cheapest upfront but unstructured - and recalled files are frequently inaccurate.</td>
            </tr>
            <tr>
              <td><strong>Self-paced platforms</strong></td>
              <td>AUD $19/month or the $89 March 2027 Pass (one-time, valid to 31 March 2027)</td>
              <td>Structured question bank, lessons and full mocks. Start immediately, cancel anytime. PassADC sits here.</td>
            </tr>
          </tbody>
        </table>

        <h2>The math that matters: total cost to pass ADC Part 1</h2>
        <p>Here's what the total ADC Part 1 cost actually looks like across two realistic scenarios:</p>
        <table>
          <thead><tr><th>Scenario</th><th>Prep</th><th>Attempts</th><th>Total (AUD)</th></tr></thead>
          <tbody>
            <tr><td>Structured prep, pass first attempt</td><td>~$400</td><td>1</td><td>~$3,150</td></tr>
            <tr><td>Self-study only, fail once, pass second</td><td>~$100</td><td>2</td><td>~$5,000</td></tr>
          </tbody>
        </table>
        <p>
          The <strong>$89 March 2027 Pass</strong> (one-time, valid to 31 March 2027) costs less than{" "}
          <strong>10%</strong> of a single failed ADC exam attempt (AUD $2,122). If structured practice,
          full-length simulations and weak-area targeting meaningfully improve your first-attempt odds, the
          prep pays for itself many times over - before you even count the emotional and career cost of
          waiting months for another sitting.
        </p>

        <h2>How to reduce ADC Part 1 costs (without hurting your chances)</h2>
        <ol>
          <li><strong>Start with a free readiness check</strong> before spending anything on prep. Know where you actually stand across every discipline in the ADC blueprint - a <Link to="/diagnostic">free ADC diagnostic</Link> takes 30 questions.</li>
          <li><strong>Choose self-paced prep if a fixed course calendar doesn't suit you.</strong> Self-paced platforms typically deliver the same MCQs, lessons and mocks at a fraction of the price, on your timeline.</li>
          <li><strong>Sit at least one full-length timed mock</strong> before booking the real exam. A failed exam costs AUD $2,122; a failed mock costs 4 hours.</li>
          <li><strong>Book your ADC exam window only when you're consistently scoring at readiness level</strong> - not before. Every avoided resit is $2,122 back in your pocket.</li>
        </ol>

        <h2>Related ADC Part 1 guides</h2>
        <p>
          See <Link to="/guides/adc-part-1-pass-rate">the real ADC Part 1 pass rate</Link> and what
          separates first-attempt passers, or read <Link to="/guides/how-to-pass-adc-part-1">how to pass
          ADC Part 1 first time</Link>. Understand the exam itself in our{" "}
          <Link to="/guides/what-is-the-adc-part-1">ADC Part 1 format guide</Link>, plan the next 8 weeks
          with the <Link to="/guides/adc-part-1-study-plan">ADC Part 1 study plan</Link>, or dive into any{" "}
          <Link to="/guides">subject-by-subject guide</Link> to master the high-weight topics first.
          Country-specific cost breakdowns:{" "}
          <Link to="/guides/adc-exam-cost-india">India (₹)</Link> ·{" "}
          <Link to="/guides/adc-exam-cost-pakistan">Pakistan (PKR)</Link>.
        </p>
        <p>
          For the full breakdown of official ADC fees, resit costs and how to judge prep, read{" "}
          <Link to="/guides/what-the-adc-exam-really-costs-2026">What the ADC exam really costs in 2026</Link>.
        </p>
      </>
    ),
  },
  "adc-part-1-pass-rate": {
    title: "ADC Written Exam Pass Rate: Official ADC Results 2023-24 and 2024-25",
    description: "Official ADC written examination pass rates from the ADC Annual Report: 41% in 2023-24 and 12% in 2024-25 (all candidates). What the numbers mean, what they don't, and how to prepare.",
    faqs: [
      ["What is the ADC Part 1 pass rate in 2026?", "The ADC Annual Report 2024-25 reports an overall written-examination pass rate of 12% for 2024-25 (404 of 3,325 candidates), down from 41% in 2023-24 (1,209 of 2,925). These are all-candidate figures, not first-attempt rates - the ADC does not publish first-attempt data or a cause for the decline."],
      ["Why is the ADC Part 1 pass rate so low?", "The exam is broad rather than tricky. It tests 13 disciplines at the level of a newly graduated Australian dentist, and questions are scenario-based rather than fact-recall. Candidates who rely on re-reading textbooks or memorising recalled files without structured MCQ practice tend to underperform."],
      ["Is the ADC Part 1 harder than the NDEB or PLAB?", "The ADC Part 1 is broadly comparable in difficulty to the Canadian NDEB written exam and slightly wider in scope than the UK's ORE Part 1. All three test scenario-based clinical knowledge across dentistry, but the ADC's heavy weighting of restorative dentistry (14%), paediatrics/orthodontics and preventive dentistry (10% each) - plus the requirement to pass all four competency clusters - gives it a distinctly wide scope."],
      ["What percentage do I need to pass ADC Part 1?", "The ADC uses a modified Angoff standard-setting method rather than a fixed percentage. Practically, candidates who consistently score above 65-70% on high-quality practice MCQs and above 60% on full-length timed mocks tend to be exam-ready."],
      ["How many attempts do most people take to pass ADC Part 1?", "The ADC does not publish attempt-level data, so no reliable first-attempt or resit breakdown exists. There is no limit on attempts and a pass is valid for five years, but each sitting costs AUD $2,122 - so preparing to pass once is far cheaper than retry cycles."],
      ["What separates candidates who pass ADC Part 1 from those who fail?", "Four things: (1) they diagnose weak subjects before studying rather than starting on page one of a textbook; (2) they practise scenario-based MCQs, not isolated facts; (3) they sit at least one full-length timed mock before booking the exam; (4) they only book the exam once their mock scores consistently exceed readiness thresholds."],
      ["Does the ADC pass rate change year to year?", "Yes, substantially. The ADC reported 41% for 2023-24 and 12% for 2024-25 while candidate numbers rose from 2,925 to 3,325. The published data shows the fall but does not establish its cause."],
      ["How can I improve my chances of passing ADC Part 1 first time?", "Take a diagnostic to know your weakest high-weight subjects. Build a targeted 8-12 week study plan around those. Practise scenario-based MCQs daily (200-400+ questions/week). Sit at least 2 full-length timed mocks before booking. Only book the exam when your mock scores are consistently at readiness level."],
    ],
    body: (
      <>
        <PassRateAnswerBox />
        <p>
          The <strong>ADC Part 1 pass rate</strong> is one of the most-searched questions on the Australian
          Dental Council pathway - and one of the least honestly answered. Here's what the historical data
          actually says, why the pass rate is what it is, and what separates the candidates who pass
          from the majority who don't.
        </p>

        <h2>ADC Part 1 pass rate: the headline numbers</h2>
        <p>
          The Australian Dental Council publishes written-examination outcomes in its annual report. The two most
          recent financial years (all candidates, not first-attempt only):
        </p>
        <table>
          <thead>
            <tr><th>Financial year</th><th>Candidates</th><th>Passed</th><th>Failed</th><th>Pass rate</th></tr>
          </thead>
          <tbody>
            <tr><td>2023-24</td><td>2,925</td><td>1,209</td><td>1,716</td><td><strong>41%</strong></td></tr>
            <tr><td>2024-25</td><td>3,325</td><td>404</td><td>2,921</td><td><strong>12%</strong></td></tr>
          </tbody>
        </table>
        <p style={{ fontSize: "0.85em" }}>
          Source: <a href="https://www.adc.org.au/files/corporate/annual-reports/ADC_Annual_Report_2024_2025.pdf" target="_blank" rel="noopener noreferrer">ADC Annual Report 2024-2025</a>. Figures are overall candidate performance and should not be read as first-attempt pass rates; the ADC does not publish first-attempt data.
        </p>
        <p>
          The <strong>practical examination (Part 2) pass rate was 11% in 2024-25 (205 of 1,880 candidates), down from 15% in 2023-24.</strong> Source: <a href="https://www.adc.org.au/files/corporate/annual-reports/ADC_Annual_Report_2024_2025.pdf" target="_blank" rel="noopener noreferrer">ADC Annual Report 2024-25</a>.
        </p>
        <h3>Why did the pass rate fall?</h3>
        <p>
          The ADC's data shows the decline but does not establish its cause. Candidate numbers rose by about 14%
          in the same year and the exam continued to use the same blueprint and per-cluster pass standard. We won't
          speculate beyond that - but the practical implication is clear: <strong>preparation quality now matters
          more than at any point in the exam's recent history.</strong>
        </p>

        <h2>Why the ADC Part 1 pass rate is so low</h2>
        <p>The exam isn't tricky for the sake of being tricky. It's hard for four specific reasons:</p>
        <ol>
          <li><strong>It's <em>wide</em>, not <em>deep</em>.</strong> 13 disciplines. 280 questions. Four clusters you must each pass. A candidate can be brilliant at endodontics and still lose the exam on restorative dentistry, paediatrics/orthodontics and preventive dentistry - which together carry 34% of the marks.</li>
          <li><strong>Scenario-based MCQs punish memorisation.</strong> The typical vignette gives you a case + 5 linked questions. If you can only recall isolated facts, you're guessing on the clinical reasoning.</li>
          <li><strong>Stamina matters.</strong> 140 questions in a day, over two days, is a physical event. Candidates who've never sat a full 4-hour timed mock hit a wall on section 3 or 4.</li>
          <li><strong>Recalled question files are misleading.</strong> Many candidates prep by reading &quot;recalled&quot; question files from previous years. These are typically incomplete, occasionally wrong, and never reflect the current blueprint weighting.</li>
        </ol>

        <h2>What candidates who pass tend to do differently</h2>
        <p>
          The ADC's latest overall written-exam pass rate is 12% (2024-25, 404 of 3,325 candidates), and the
          ADC does not publish first-attempt pass rates. Across candidate patterns and post-exam feedback, four
          behaviours consistently show up in candidates who pass:
        </p>
        <ul>
          <li><strong>They diagnose before they study.</strong> They don't start on page one of a textbook. They take a balanced diagnostic across every discipline in the ADC blueprint, rank weak subjects by weighting × accuracy deficit, and spend their first month on that priority list.</li>
          <li><strong>They practise scenario-based MCQs daily.</strong> 200-400+ questions per week, not just reading. And they review the explanation for every wrong answer the same day.</li>
          <li><strong>They sit full-length timed mocks before the exam.</strong> At least one, ideally two - under real conditions (same start time, timed breaks, no pausing). The <Link to="/guides/adc-part-1-study-plan">8-week study plan</Link> builds these in.</li>
          <li><strong>They only book the exam when their mock scores are consistently at readiness level.</strong> Booking an exam &quot;because it's coming up&quot; when mock scores say &quot;not yet&quot; is how AUD $2,122 gets burned.</li>
        </ul>

        <CTA label="Take the free readiness check" />

        <h2>The economics of the ADC Part 1 pass rate</h2>
        <p>
          The pass rate isn't just an academic statistic - it has direct financial consequences. Each failed
          attempt costs another <strong>AUD $2,122 in exam fees</strong>, plus another 6-12 months of
          waiting, working on temporary visas, and delayed career progression. Structured preparation is
          almost always cheaper than an extra attempt.
        </p>
        <p>
          See our full <Link to="/guides/adc-part-1-cost">ADC Part 1 cost breakdown</Link> for the total
          financial picture across single-attempt and multi-attempt scenarios.
        </p>

        <h2>Pass rate by subject: where marks are actually lost</h2>
        <p>
          Candidate self-reports and diagnostic-tool aggregate data consistently show four subjects where
          overseas-trained dentists lose the most marks against the ADC's expected level:
        </p>
        <table>
          <thead>
            <tr><th>Subject</th><th>Weighting</th><th>Typical performance gap</th></tr>
          </thead>
          <tbody>
            <tr><td>Oral Surgery</td><td>7%</td><td>Australian medico-legal / consent framing catches candidates trained overseas</td></tr>
            <tr><td>Community Dentistry</td><td>—</td><td>Australia-specific public health systems (DHSV, PBS, etc.) unfamiliar to most</td></tr>
            <tr><td>Pharmacology</td><td>10%</td><td>PBS listings and Australian prescribing guidelines differ from most home countries</td></tr>
            <tr><td>Prosthodontics</td><td>5% + 5%</td><td>Fixed and removable prosthodontics combined; wide sub-topic range makes this a big swing area</td></tr>
          </tbody>
        </table>
        <p>
          If you're weak in any of the first three, targeted
          study on Australian-specific content pays off disproportionately.
        </p>

        <h2>Improving your personal pass probability</h2>
        <p>The framework that consistently moves candidates from &quot;might pass&quot; to &quot;first-attempt pass&quot;:</p>
        <ol>
          <li>Take a diagnostic across every discipline in the ADC blueprint - know your gaps before you buy anything.</li>
          <li>Study the 3-4 weakest high-weight subjects first, not the ones you already enjoy.</li>
          <li>200-400 scenario-based MCQs per week, with explanations reviewed the same day.</li>
          <li>2 full-length timed mocks in the final 2-3 weeks. Don't book the exam until you're passing them.</li>
          <li>Final week: review flagged questions only. No new content. Sleep.</li>
        </ol>

        <p>
          Candidates who follow this framework give themselves the best possible chance against a
          low overall pass rate. The exam's pass rate is a reflection of <em>how</em> most
          candidates prepare, not how hard the exam actually is.
        </p>

        <h2>Related guides</h2>
        <p>
          Understand the exam itself in our <Link to="/guides/what-is-the-adc-part-1">ADC Part 1 format
          guide</Link>, plan your prep with the <Link to="/guides/adc-part-1-study-plan">8-week study
          plan</Link>, and budget realistically with the <Link to="/guides/adc-part-1-cost">ADC Part 1 cost
          breakdown</Link>.
        </p>
      </>
    ),
  },
  "how-to-pass-adc-part-1": {
    title: "How to Pass the ADC Part 1 Exam: The First-Attempt Playbook (2026)",
    description: "The complete first-attempt playbook for passing the ADC Part 1 written exam. 8-step framework covering diagnostics, targeted study, scenario MCQs, timed mocks, and exam-day tactics for overseas-trained dentists.",
    faqs: [
      ["How do I pass the ADC Part 1 exam on the first attempt?", "First-attempt success comes down to four things: (1) diagnose your weak subjects before you start studying; (2) prioritise high-weight weak subjects first; (3) practise 200-400 scenario-based MCQs weekly with same-day explanation review; (4) sit at least 2 full-length timed mocks and only book the exam once your mock scores are consistently at readiness level."],
      ["How long do I need to study for ADC Part 1?", "Most working dentists need 8-12 weeks at 1.5-2 focused hours per day. Full-time preparation can compress this to 6-8 weeks. Longer prep isn't better if it's unstructured - a targeted 10-week plan usually beats 6 months of unfocused study."],
      ["What is the best study strategy for ADC Part 1?", "The diagnose-prioritise-practise-simulate loop. Diagnose gaps across every discipline in the ADC blueprint. Prioritise weak subjects by weight × accuracy deficit. Practise with scenario-based MCQs plus lesson gaps daily. Simulate exam day with at least two full-length timed mocks in the final 2-3 weeks."],
      ["Should I buy a course or self-study for ADC Part 1?", "It depends on how you learn. Cohort coaching courses (~US$1,050+) give structure but move on your schedule, not yours. Self-study is cheapest but easiest to derail. Self-paced platforms with diagnostics, MCQs and mocks (from AUD $19/month) usually give the best structure-per-dollar for working dentists."],
      ["What are the biggest mistakes ADC Part 1 candidates make?", "The five most common: (1) starting on page one of a textbook instead of diagnosing; (2) over-relying on recalled question files; (3) studying subjects they enjoy rather than subjects that cost them marks; (4) booking the exam before their mock scores are at readiness level; (5) skipping full-length timed mocks and hitting a stamina wall on exam day."],
      ["When should I book my ADC Part 1 exam?", "Book only after you've consistently hit readiness-level scores on at least two full-length timed mocks. Booking earlier because the next window is closing is one of the top reasons candidates fail - the exam fee (AUD $2,122 per attempt) doesn't reward optimism."],
      ["What should I do the week before the ADC Part 1 exam?", "Only review flagged questions and personal takeaways from earlier mocks and practice. No new content. Sit one final timed mock 3-4 days out. Sleep 7-8 hours nightly for the final week. Cut caffeine down. On exam day, arrive early, hydrate, and don't leave any question blank - there's no penalty for wrong answers."],
      ["What if I've already failed ADC Part 1 once?", "Failing once doesn't mean failing again. Take a fresh diagnostic, identify what specifically dropped marks last time (usually 2-3 subject blocks), and build the next 6-8 weeks around those. The ADC doesn't publish pass rates by attempt, so there's no official figure for resits - what helps is a plan built around the disciplines you were weakest in."],
    ],
    body: (
      <>
        <p>
          The <strong>ADC Part 1</strong> is passable - even on the first attempt - for any competent
          overseas-trained dentist who prepares the right way. The majority who don't pass aren't
          less capable; they usually just prepared with the wrong approach. This is the first-attempt
          playbook that consistently works.
        </p>

        <h2>The 8-step ADC Part 1 first-attempt framework</h2>
        <p>Every step matters. Skip any of them and your pass probability drops noticeably.</p>

        <h3>Step 1 - Diagnose before you study</h3>
        <p>
          Do not start on page one of a textbook. The single biggest reason candidates run out of time to
          prep the subjects that actually cost them marks is that they spend the first three weeks
          reviewing what they already know.
        </p>
        <p>
          Sit a balanced diagnostic across every discipline in the ADC blueprint. Rank your accuracy per subject against the
          blueprint weighting. Your priority list = high weighting × low accuracy. Oral Surgery at 55%
          accuracy beats Community Dentistry at 40% - it carries far more of the marks.
        </p>

        <h3>Step 2 - Build a subject priority list</h3>
        <p>
          After the diagnostic, list your weakest disciplines in priority order. The top 3 subjects on that list
          will consume roughly 50-60% of your study time. Everything else gets touched but not obsessed
          over. Read the full <Link to="/guides/what-is-the-adc-part-1">ADC blueprint and subject
          weighting</Link> to know exactly how many marks each subject is worth.
        </p>

        <h3>Step 3 - Master weaknesses with a pre/post-test loop</h3>
        <p>Each lesson gets three passes:</p>
        <ul>
          <li><strong>Pre-test</strong> - 5-10 MCQs on the topic before reading anything. Tells you what you already know.</li>
          <li><strong>Learn</strong> - read the concept-focused lesson, not the whole chapter.</li>
          <li><strong>Post-test</strong> - 5-10 more MCQs on the same topic. Anything you get wrong gets flagged for spaced review.</li>
        </ul>
        <p>
          This loop is what separates &quot;I studied it&quot; from &quot;I actually know it&quot;. It's built into most modern
          self-paced platforms - including PassADC.
        </p>

        <h3>Step 4 - Daily scenario-based MCQs (200-400/week)</h3>
        <p>
          The ADC is a scenario exam. A patient walks in with X, you interpret, you decide. That skill can
          only be trained by doing scenario MCQs, not by re-reading textbooks. Target:
        </p>
        <ul>
          <li>30-50 MCQs per day, 5-6 days per week</li>
          <li>Every wrong answer gets its explanation read the same day</li>
          <li>Flag your wrong answers into a personal review deck</li>
        </ul>

        <CTA label="Try 6 free ADC-style questions now" />

        <h3>Step 5 - Interleave and space-repeat</h3>
        <p>
          After the first 3-4 weeks of subject-focused work, start <em>interleaving</em> - mix subjects
          within each practice session rather than doing single-subject days. Interleaving feels harder,
          and that's precisely why it works for a mixed-format exam.
        </p>
        <p>
          In parallel, run spaced-repetition reviews of everything you got wrong in weeks 1-4. Anki-style
          scheduling works, but a built-in review queue on a study platform saves the setup time.
        </p>

        <h3>Step 6 - Two full-length timed mocks</h3>
        <p>
          This is the step most failed candidates skip. The ADC Part 1 is a stamina event: 140 questions
          in a day, over two days. If exam day is the first time you sit a full 4-hour timed session,
          you'll hit a cognitive wall in section 3 or 4. Sit at least two full mocks under real conditions:
        </p>
        <ul>
          <li>Same start time as your booked exam</li>
          <li>Timed breaks only - no pausing</li>
          <li>No phones, no reference material</li>
          <li>Full post-mortem the following day: every wrong answer's explanation, patterns of error</li>
        </ul>

        <h3>Step 7 - Only book the exam when your mock scores say so</h3>
        <p>
          Book only after you've consistently hit readiness-level scores on at least two full mocks.
          Booking earlier because &quot;the next window is closing&quot; is how AUD $2,122 gets burned. Read the
          full <Link to="/guides/adc-part-1-cost">cost breakdown</Link> - a failed attempt effectively
          doubles your total ADC spend.
        </p>

        <h3>Step 8 - The final week: taper, don't cram</h3>
        <ul>
          <li>Review flagged questions and personal takeaways only. No new content.</li>
          <li>One final timed mock 3-4 days before the exam.</li>
          <li>Sleep 7-8 hours nightly. Cut caffeine down.</li>
          <li>Confirm your Pearson VUE test centre logistics 48 hours out.</li>
          <li>Exam day: arrive early, hydrate, never leave a question blank (no penalty for wrong answers).</li>
        </ul>

        <h2>The five most common ADC Part 1 preparation mistakes</h2>
        <ol>
          <li><strong>Starting on page one of a textbook.</strong> You'll spend three weeks re-reading what you know while your weak subjects rot.</li>
          <li><strong>Relying on recalled question files.</strong> They're incomplete, sometimes wrong, and don't reflect the current blueprint.</li>
          <li><strong>Studying subjects you enjoy, not subjects that cost you marks.</strong> Every candidate has a favourite topic. It's usually not where their marks are being lost.</li>
          <li><strong>Booking the exam before you're ready.</strong> Emotion-driven exam bookings are the single biggest cause of first-attempt failure.</li>
          <li><strong>Skipping full-length mocks.</strong> Fitness is trained; it isn't willed on the day.</li>
        </ol>

        <h2>How long does ADC Part 1 preparation actually take?</h2>
        <p>
          For most working dentists prepping 1.5-2 hours/day, six days a week, expect{" "}
          <strong>8-12 focused weeks</strong>. Full-time preparation can compress this to 6-8 weeks, but
          the plateau is real: past week 10, hours-in doesn't produce many extra marks-out. If you've been
          studying six months already and still not passing mocks, more time isn't the fix - the
          <em> approach</em> is.
        </p>

        <h2>What if you've already failed once?</h2>
        <p>
          Failing once doesn't mean failing again. The ADC doesn't publish pass rates by attempt, so
          there's no official figure for resits. What helps is a plan built around the disciplines you
          were weakest in. The recovery framework:
        </p>
        <ol>
          <li>Take a fresh diagnostic. Don't assume you know what dropped marks - most candidates are wrong about their own weak areas.</li>
          <li>Study the specific subject blocks the exam exposed, not the entire syllabus again.</li>
          <li>Sit more full-length mocks this time - three or four, not two.</li>
          <li>Don't rebook the exam until mock scores are consistently at readiness level.</li>
        </ol>

        <h2>Ready to start?</h2>
        <p>
          Everything above is executable on any structured prep platform - including PassADC. Start with a
          free <Link to="/readiness-check">readiness snapshot</Link> to see where you actually stand across
          every discipline in the ADC blueprint. From there, the plan writes itself.
        </p>
      </>
    ),
  },
  "adc-exam-cost-india": {
    title: "ADC Exam Cost for Indian Dentists 2026 (Fees in ₹, Total Budget, Payment Tips)",
    description: "Complete 2026 ADC exam cost guide for Indian dentists: initial assessment + written exam fees in AUD and INR, realistic total prep budget in rupees, forex tips, and how to reduce total spend without hurting first-attempt chances.",
    faqs: [
      ["How much does the ADC Part 1 exam cost for Indian dentists in 2026?", "The ADC Part 1 written exam is AUD $2,122 (~₹1,15,000) per attempt, plus a separate initial assessment fee of ~AUD $647 (~₹35,500). Total ADC official fees: ~AUD $2,800 (~₹1,50,500) before any preparation costs."],
      ["What is the total budget for an Indian dentist doing ADC in 2026?", "Realistic total budget: ₹2,20,000 to ₹3,80,000 depending on prep approach. This includes ~₹1,50,500 in ADC official fees, ₹15,000 to ₹1,60,000 in preparation costs, and ~₹40,000 for exam-day logistics if travelling from India (many candidates sit locally at Pearson VUE Chennai/Delhi/Mumbai)."],
      ["Can Indian dentists sit the ADC Part 1 exam in India?", "Yes. The ADC written exam is delivered at Pearson VUE test centres worldwide, including Chennai, Delhi, Mumbai, Bengaluru and Hyderabad. You do not need to travel to Australia for the written exam. The Part 2 practical exam, when you get to it, is held in Australia."],
      ["How do Indian dentists pay ADC exam fees?", "The ADC bills in Australian Dollars (AUD). Most Indian candidates use their existing Indian debit/credit card, but rupee-denominated cards can incur 3-5% FX markup plus GST on the conversion. Some candidates use forex cards (Niyo, IDFC First, Federal Bank) with lower markup. Confirm with your bank before applying."],
      ["Is there a way to reduce ADC exam costs for Indian dentists?", "The single biggest saving is passing on the first attempt. A repeat exam costs another ~₹1,15,000 in fees alone. Self-paced prep platforms (~₹10,000-15,000 for 6 months) cost a fraction of cohort coaching courses (~₹85,000+) while covering the same MCQ practice, mocks and lessons."],
      ["What is the ADC initial assessment fee in INR?", "The ADC initial assessment of qualifications is approximately AUD $647, which is roughly ₹35,500 at current exchange rates. This is separate from and paid before the written exam application fee."],
      ["Can I get a loan for ADC exam costs in India?", "Some Indian education loan providers (Credila, HDFC Credila, InCred) cover overseas exam and preparation costs. Personal loans from private banks are also common. Because the total ADC budget is significantly smaller than an MBBS/MDS abroad, most candidates self-fund."],
    ],
    body: (
      <>
        <p>
          For Indian dentists, the <strong>ADC pathway is one of the most cost-effective routes to
          practising overseas</strong> - but only if you budget realistically and prep to pass first
          time. Here's the complete 2026 cost breakdown in rupees.
        </p>

        <h2>ADC Part 1 exam cost in INR - the official fees</h2>
        <table>
          <thead>
            <tr><th>Fee</th><th>AUD</th><th>INR (approx.)</th></tr>
          </thead>
          <tbody>
            <tr><td>Initial assessment of qualifications</td><td>~$647</td><td>~₹35,500</td></tr>
            <tr><td>ADC written exam application fee (per attempt)</td><td>$2,122</td><td>~₹1,15,000</td></tr>
            <tr><td><strong>Total official ADC fees (first attempt)</strong></td><td><strong>~$2,800</strong></td><td><strong>~₹1,50,500</strong></td></tr>
          </tbody>
        </table>
        <p className="text-sm text-muted-foreground">
          INR conversions are approximate at ~₹55/AUD. The ADC bills in AUD, so your actual rupee cost
          depends on your card's FX rate on payment day. Confirm live rates at xe.com and current ADC
          fees at <a href="https://adc.org.au" target="_blank" rel="noreferrer">adc.org.au</a>.
        </p>

        <h2>Total budget for an Indian dentist doing ADC Part 1</h2>
        <p>
          Official fees are only part of the story. Realistic total budgets by preparation approach:
        </p>
        <table>
          <thead>
            <tr><th>Approach</th><th>Prep cost</th><th>Total ADC budget</th></tr>
          </thead>
          <tbody>
            <tr><td>Cohort coaching + first-attempt pass</td><td>~₹85,000</td><td>~₹2,35,500</td></tr>
            <tr><td>Pure self-study + first-attempt pass</td><td>~₹10,000</td><td>~₹1,60,500</td></tr>
            <tr><td>Self-paced platform + first-attempt pass</td><td>~₹15,000</td><td>~₹1,65,500</td></tr>
            <tr><td>Any approach + one failed attempt</td><td>+₹1,15,000 more in exam fees</td><td>Add ₹1,15,000</td></tr>
          </tbody>
        </table>
        <p>
          The single biggest cost-saving action isn't the prep tier you pick - it's <strong>passing first
          time</strong>. One failed attempt adds ₹1,15,000 in exam fees, plus another 6-12 months of
          delayed career progression while you resit. Structured prep almost always pays for itself.
        </p>

        <h2>Where Indian dentists can sit the ADC exam</h2>
        <p>
          You do <em>not</em> need to travel to Australia for the written exam. The ADC Part 1 is
          delivered at Pearson VUE test centres worldwide. Common Indian test centres:
        </p>
        <ul>
          <li>Chennai (multiple centres)</li>
          <li>Delhi / Gurgaon / Noida</li>
          <li>Mumbai</li>
          <li>Bengaluru</li>
          <li>Hyderabad</li>
          <li>Kolkata, Pune, Ahmedabad (subject to availability)</li>
        </ul>
        <p>
          Book your Pearson VUE slot as soon as your ADC exam application is approved - popular Indian
          centres in Chennai and Delhi fill 2-3 months out during peak windows.
        </p>

        <CTA label="Take the free ADC readiness snapshot" />

        <h2>Paying ADC fees from India - FX and card tips</h2>
        <p>
          The ADC bills exclusively in AUD. Your rupee cost depends on your bank's FX rate:
        </p>
        <ul>
          <li><strong>Standard Indian credit/debit cards</strong>: 3-5% forex markup + 18% GST on the markup. This adds roughly ₹4,500-7,000 on top of the exam fee.</li>
          <li><strong>Forex cards</strong> (Niyo Global, IDFC First Wow, Federal Bank Selfie): lower markup (typically 1-2%). Load in advance when INR-AUD is favourable.</li>
          <li><strong>Wire transfer</strong>: possible but usually more expensive due to correspondent bank fees. Rarely worth it for a single ADC fee.</li>
        </ul>

        <h2>How to reduce total ADC costs (without hurting first-attempt odds)</h2>
        <ol>
          <li><strong>Start with a free readiness check</strong> - know your gaps before you spend anything on prep. A <Link to="/diagnostic">free ADC diagnostic</Link> takes 30 questions and identifies your weak subjects.</li>
          <li><strong>Skip the cohort coaching premium if it doesn't fit your schedule.</strong> Self-paced platforms deliver the same MCQs, lessons and mocks at ~15-20% of the cost. Cohort courses (~₹85,000+) fix start dates you may not want.</li>
          <li><strong>Sit at least one full-length timed mock</strong> before booking the real exam. A failed exam costs ₹1,15,000; a failed mock costs 4 hours.</li>
          <li><strong>Only book the exam when your mock scores are at readiness level.</strong> Every avoided resit is ₹1,15,000 back in your pocket.</li>
          <li><strong>Book Pearson VUE early</strong> - same-city late bookings often mean travelling to another city, adding travel/hotel costs unnecessarily.</li>
        </ol>

        <h2>What comes after passing the ADC Part 1?</h2>
        <p>
          After the written exam, Indian dentists progress to the <strong>ADC Part 2 practical
          examination</strong>, held in Australia. That's when you'll need to plan Australia travel, visa
          and accommodation costs (~AUD $3,500-5,500 additional, depending on how long you stay). But
          Part 2 costs are only relevant after Part 1 is behind you.
        </p>

        <h2>Related ADC guides</h2>
        <p>
          Understand what's actually on the exam in our <Link to="/guides/what-is-the-adc-part-1">ADC Part
          1 format guide</Link>, see the <Link to="/guides/adc-part-1-pass-rate">real ADC pass rate</Link>{" "}
          and what separates first-attempt passers, and plan your prep with the{" "}
          <Link to="/guides/adc-part-1-study-plan">8-week study plan</Link>. For the general (non-Indian
          specific) cost breakdown across all currencies, see the{" "}
          <Link to="/guides/adc-part-1-cost">full ADC Part 1 cost guide</Link>.
        </p>
      </>
    ),
  },
  "adc-exam-cost-pakistan": {
    title: "ADC Exam Cost for Pakistani Dentists 2026 (Fees in PKR, Total Budget, Payment Tips)",
    description: "Complete 2026 ADC exam cost guide for Pakistani dentists: initial assessment + written exam fees in AUD and PKR, realistic total prep budget in rupees, forex tips, and how to reduce total spend without hurting first-attempt chances.",
    faqs: [
      ["How much does the ADC Part 1 exam cost for Pakistani dentists in 2026?", "The ADC Part 1 written exam is AUD $2,122 (~Rs 385,000 PKR) per attempt, plus a separate initial assessment fee of ~AUD $647 (~Rs 119,000 PKR). Total ADC official fees: ~AUD $2,800 (~Rs 504,000 PKR) before any preparation costs."],
      ["What is the total budget for a Pakistani dentist doing ADC in 2026?", "Realistic total budget: Rs 550,000 to Rs 900,000 PKR depending on preparation approach. This includes ~Rs 504,000 in ADC official fees, Rs 30,000 to Rs 200,000 in preparation costs, and any exam-day logistics costs. Most Pakistani candidates sit locally at Pearson VUE Karachi/Lahore/Islamabad."],
      ["Can Pakistani dentists sit the ADC Part 1 exam in Pakistan?", "Yes. The ADC written exam is delivered at Pearson VUE test centres worldwide, including Karachi, Lahore and Islamabad. You do not need to travel to Australia for the written exam. The Part 2 practical exam is held in Australia."],
      ["How do Pakistani dentists pay ADC exam fees?", "The ADC bills in Australian Dollars (AUD). Most Pakistani candidates need to use an international debit or credit card. Because SBP FX restrictions can affect large overseas payments, some candidates pay through relatives abroad or use hawala-alternative digital services. Confirm your payment method well before your exam application deadline."],
      ["Is there a way to reduce ADC exam costs for Pakistani dentists?", "The single biggest saving is passing on the first attempt - a repeat exam costs another ~Rs 385,000 in fees alone. Self-paced preparation platforms (~Rs 30,000-40,000 for 6 months) cost a fraction of cohort coaching courses (Rs 200,000+) while covering the same MCQ practice, mocks and lessons."],
      ["What is the ADC initial assessment fee in Pakistani rupees?", "The ADC initial assessment of qualifications is approximately AUD $647, which is roughly Rs 119,000 PKR at current exchange rates. It is a separate fee paid before the written exam application fee."],
      ["Can I get a loan for ADC exam costs in Pakistan?", "Formal education loan products for overseas exams are limited in Pakistan. Most candidates self-fund from savings, or borrow from family. Some private banks offer personal loans that can cover ADC fees, but interest rates are typically higher than in Western markets."],
    ],
    body: (
      <>
        <p>
          For Pakistani dentists, the <strong>ADC pathway offers a route to practising in Australia</strong>{" "}
          - but the total cost is real and needs planning, especially given current FX and forex-payment
          constraints. Here's the honest 2026 breakdown in PKR.
        </p>

        <h2>ADC Part 1 exam cost in PKR - the official fees</h2>
        <table>
          <thead>
            <tr><th>Fee</th><th>AUD</th><th>PKR (approx.)</th></tr>
          </thead>
          <tbody>
            <tr><td>Initial assessment of qualifications</td><td>~$647</td><td>~Rs 119,000</td></tr>
            <tr><td>ADC written exam application fee (per attempt)</td><td>$2,122</td><td>~Rs 385,000</td></tr>
            <tr><td><strong>Total official ADC fees (first attempt)</strong></td><td><strong>~$2,800</strong></td><td><strong>~Rs 504,000</strong></td></tr>
          </tbody>
        </table>
        <p className="text-sm text-muted-foreground">
          PKR conversions are approximate at ~Rs 183/AUD. The ADC bills in AUD, so your actual rupee cost
          depends on your card's FX rate on payment day. Confirm live rates at xe.com and current ADC
          fees at <a href="https://adc.org.au" target="_blank" rel="noreferrer">adc.org.au</a>.
        </p>

        <h2>Total budget for a Pakistani dentist doing ADC Part 1</h2>
        <p>Realistic total budgets by preparation approach:</p>
        <table>
          <thead>
            <tr><th>Approach</th><th>Prep cost</th><th>Total ADC budget</th></tr>
          </thead>
          <tbody>
            <tr><td>Cohort coaching + first-attempt pass</td><td>~Rs 200,000</td><td>~Rs 704,000</td></tr>
            <tr><td>Pure self-study + first-attempt pass</td><td>~Rs 20,000</td><td>~Rs 524,000</td></tr>
            <tr><td>Self-paced platform + first-attempt pass</td><td>~Rs 35,000</td><td>~Rs 539,000</td></tr>
            <tr><td>Any approach + one failed attempt</td><td>+Rs 385,000 more in exam fees</td><td>Add Rs 385,000</td></tr>
          </tbody>
        </table>
        <p>
          The single biggest cost-saving action is <strong>passing first time</strong>. One failed attempt
          adds Rs 385,000 in exam fees, plus another 6-12 months of career delay. Structured preparation
          almost always pays for itself.
        </p>

        <h2>Where Pakistani dentists can sit the ADC exam</h2>
        <p>
          You do <em>not</em> need to travel to Australia for the written exam. Pearson VUE Pakistan test
          centres include:
        </p>
        <ul>
          <li>Karachi (multiple centres)</li>
          <li>Lahore</li>
          <li>Islamabad / Rawalpindi</li>
          <li>Peshawar (subject to availability)</li>
        </ul>
        <p>
          Book your Pearson VUE slot as soon as your ADC exam application is approved. Slots can fill
          during peak windows.
        </p>

        <CTA label="Take the free ADC readiness snapshot" />

        <h2>Paying ADC fees from Pakistan - FX and payment tips</h2>
        <p>
          Payment logistics are more constrained in Pakistan than in most countries. The ADC bills only in
          AUD, so:
        </p>
        <ul>
          <li><strong>International debit or credit card</strong>: The standard route. Confirm with your bank that overseas transactions of AUD $2,122 are enabled - many Pakistani cards have low default limits or require pre-approval.</li>
          <li><strong>Family abroad</strong>: Some candidates pay through relatives in the UK, UAE or Australia who then get reimbursed via local channels.</li>
          <li><strong>Digital forex services</strong>: Wise, Payoneer and similar work but check current SBP guidance before large outbound transactions.</li>
          <li><strong>Currency exchange timing matters</strong>: PKR has been volatile against AUD. If FX moves 5%, that's ~Rs 20,000 on the exam fee alone.</li>
        </ul>

        <h2>How to reduce total ADC costs (without hurting first-attempt odds)</h2>
        <ol>
          <li><strong>Start with a free readiness check</strong> - know your gaps before spending on prep. A <Link to="/diagnostic">free ADC diagnostic</Link> takes 30 questions across every discipline in the ADC blueprint.</li>
          <li><strong>Skip cohort coaching if the calendar doesn't fit.</strong> Self-paced platforms typically deliver the same MCQs, lessons and mocks at 15-20% of cohort cost. Cohort courses (Rs 200,000+) also fix start dates you may not want.</li>
          <li><strong>Sit at least one full-length timed mock</strong> before booking the real exam. A failed exam costs Rs 385,000; a failed mock costs 4 hours.</li>
          <li><strong>Only book the exam when your mock scores are at readiness level.</strong> Every avoided resit is Rs 385,000 back in your pocket.</li>
        </ol>

        <h2>What comes after passing the ADC Part 1?</h2>
        <p>
          After the written exam, Pakistani dentists progress to the ADC Part 2 practical examination,
          which is held in Australia. Part 2 requires additional budget for Australia travel, visa and
          accommodation costs (~AUD $3,500-5,500 additional). Part 2 costs are only relevant after Part 1
          is complete.
        </p>

        <h2>Related ADC guides</h2>
        <p>
          Understand what's on the exam in our <Link to="/guides/what-is-the-adc-part-1">ADC Part 1
          format guide</Link>, see the <Link to="/guides/adc-part-1-pass-rate">real ADC pass rate</Link>{" "}
          and what separates first-attempt passers, and plan your prep with the{" "}
          <Link to="/guides/adc-part-1-study-plan">8-week study plan</Link>. For the general (non-Pakistani
          specific) cost breakdown, see the <Link to="/guides/adc-part-1-cost">full ADC Part 1 cost guide</Link>.
        </p>
      </>
    ),
  },
};

const SubjectGuideArticle = ({ guide }) => (
  <>
    <p>
      <strong>{guide.subject}</strong> carries roughly <strong>{guide.weight}</strong> of the ADC Part 1
      written exam - {guide.approx}. Below: the high-yield topics to master, the classic traps that cost
      candidates marks, an ADC-style example question, and how to study this subject efficiently.
    </p>

    <h2>High-yield topics for {guide.subject}</h2>
    <table>
      <thead><tr><th>Topic</th><th>What to focus on</th></tr></thead>
      <tbody>
        {guide.topics.map(([t, d]) => <tr key={t}><td><strong>{t}</strong></td><td>{d}</td></tr>)}
      </tbody>
    </table>

    {guide.struggles && guide.struggles.length > 0 && (
      <>
        <h2>Common areas candidates struggle with</h2>
        <ul>{guide.struggles.map((s) => <li key={s}>{s}</li>)}</ul>
      </>
    )}

    <h2>Classic exam traps</h2>
    <ul>{guide.traps.map((t) => <li key={t}>{t}</li>)}</ul>

    {guide.example && (
      <>
        <h2>Example ADC-style question</h2>
        <div className="not-prose rounded-2xl border border-border bg-card p-5 my-4">
          <p className="text-sm font-semibold mb-3">Question</p>
          <p className="text-sm mb-4">{guide.example.question}</p>
          <ol className="space-y-2 mb-4 pl-6 list-[upper-alpha]">
            {guide.example.options.map((opt) => (
              <li key={opt} className="text-sm">{opt}</li>
            ))}
          </ol>
          <div className="rounded-xl bg-success/10 border border-success/25 p-3 text-sm">
            <p className="font-semibold text-success mb-1">Answer: {guide.example.answer}</p>
            <p className="text-muted-foreground">{guide.example.why}</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          This is one worked example. There are hundreds more scenario-based {guide.subject.toLowerCase()}{" "}
          MCQs inside the PassADC question bank - with per-option explanations on the ones that matter most.
        </p>
      </>
    )}

    {guide.checklist && guide.checklist.length > 0 && (
      <>
        <h2>Key concepts to revise</h2>
        <ul>{guide.checklist.map((c) => <li key={c}>{c}</li>)}</ul>
      </>
    )}

    <h2>How to study {guide.subject} for the ADC Part 1</h2>
    <ul>{guide.tips.map((t) => <li key={t}>{t}</li>)}</ul>

    <h2>How to test your readiness in {guide.subject}</h2>
    <p>
      The exam tests this subject through clinical vignettes, so practise it the same way - scenario-based
      MCQs with explanations, not isolated fact recall. The fastest way to know whether{" "}
      {guide.subject.toLowerCase()} is a strength or a gap is to take a diagnostic across every discipline in the ADC blueprint
      and compare your accuracy in this subject against the exam weighting.
    </p>
    <p className="not-prose">
      Once you know where you stand, target the weak areas above with daily practice, review every wrong
      answer's explanation the same day, and sit at least two full-length timed mocks before booking the
      real exam.
    </p>

    <div className="not-prose my-8 rounded-2xl border border-primary/25 bg-primary/5 p-6 text-center">
      <p className="font-semibold mb-1">Want to see whether {guide.subject.toLowerCase()} is actually one of your weak areas?</p>
      <p className="text-sm text-muted-foreground mb-4">6 ADC-style questions across the top subjects. Free, instant, no credit card.</p>
      <Link
        to="/readiness-check"
        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold hover:opacity-90"
        data-testid="subject-guide-readiness-cta"
      >
        Check my ADC readiness <ArrowRight className="w-4 h-4" />
      </Link>
    </div>

    <h2>Related ADC Part 1 resources</h2>
    <p>
      Start with the <Link to="/adc-part-1">ADC Part 1 preparation hub</Link>, plan the next 8 weeks
      with the <Link to="/adc-part-1/study-guide">study guide</Link>, or drill more{" "}
      {guide.subject.toLowerCase()} MCQs in the{" "}
      <Link to="/adc-part-1/questions">question bank</Link>. Also see the full{" "}
      <Link to="/guides/what-is-the-adc-part-1">ADC Part 1 exam format guide</Link> and{" "}
      <Link to="/guides/how-to-pass-adc-part-1">how to pass ADC Part 1</Link>.
    </p>
  </>
);

const guideMeta = (s) => {
  if (GUIDES[s]) return { title: GUIDES[s].title, description: GUIDES[s].description };
  const g = SUBJECT_GUIDES[s];
  return { title: `${g.subject} for ADC Part 1: High-Yield Topics & Exam Traps`, description: g.description };
};

const relatedSlugs = (slug) => {
  const subjects = Object.keys(SUBJECT_GUIDES);
  const cores = Object.keys(GUIDES);
  if (SUBJECT_GUIDES[slug]) {
    const i = subjects.indexOf(slug);
    return [subjects[(i + 1) % subjects.length], subjects[(i + 2) % subjects.length], "adc-part-1-study-plan"];
  }
  return [...cores.filter((c) => c !== slug), subjects[0]].slice(0, 3);
};

const RelatedGuides = ({ slug }) => (
  <div className="mt-4" data-testid="related-guides">
    <h2 className="text-lg font-bold mb-4">Keep reading</h2>
    <div className="grid sm:grid-cols-3 gap-3">
      {relatedSlugs(slug).map((s) => {
        const m = guideMeta(s);
        return (
          <Link key={s} to={`/guides/${s}`} data-testid={`related-guide-${s}`}
            className="block rounded-2xl border border-border bg-card p-4 hover:border-primary/40 transition-colors">
            <p className="font-semibold text-sm leading-snug">{m.title}</p>
            <p className="text-xs text-muted-foreground mt-1.5 line-clamp-3">{m.description}</p>
          </Link>
        );
      })}
    </div>
  </div>
);

export default function GuidesPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const guide = slug ? GUIDES[slug] : null;
  const subjectGuide = slug && !guide ? SUBJECT_GUIDES[slug] : null;
  const seoEntry = slug ? GUIDE_SEO[`/guides/${slug}`] : null;

  const pageTitle = seoEntry
    ? seoEntry.title
    : guide
    ? `${guide.title} | PassADC`
    : subjectGuide
      ? `ADC Part 1 ${subjectGuide.subject} Guide: High-Yield Topics, Example Questions & Study Tips | PassADC`
      : "ADC Part 1 Exam Guides | PassADC";
  const pageDescription = seoEntry?.description || guide?.description || subjectGuide?.description ||
    "Free ADC Part 1 exam guides for overseas-trained dentists: exam format, study plans, costs and subject-by-subject preparation.";
  useGuideSeo(slug, pageTitle, pageDescription, { faqs: guide?.faqs || subjectGuide?.faqs });

  useEffect(() => {
    if (slug && !guide && !subjectGuide) navigate("/guides", { replace: true });
  }, [slug, guide, subjectGuide, navigate]);

  if (slug && !guide && !subjectGuide) return null;

  if (!slug) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto px-6 py-16" data-testid="guides-index">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="w-4 h-4" /> passadc.com
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">ADC Part 1 Exam Guides</h1>
          <p className="text-muted-foreground mb-6">Free, no-fluff guides for overseas-trained dentists preparing for the ADC written exam.</p>
          <div className="mb-10 rounded-2xl border border-primary/25 bg-primary/5 p-5">
            <p className="font-semibold mb-1">Just here to prepare?</p>
            <p className="text-sm text-muted-foreground mb-3">Start with the complete <Link to="/adc-part-1" className="underline underline-offset-4 text-primary">ADC Part 1 preparation hub</Link> - the question bank, mock exam and 8-week study guide are all in one place.</p>
          </div>
          <div className="space-y-4">
            {Object.entries(GUIDES).map(([s, g]) => (
              <Link key={s} to={`/guides/${s}`} data-testid={`guide-link-${s}`}
                className="block rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition-colors">
                <div className="flex items-start gap-3">
                  <BookOpen className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="font-semibold">{g.title}</h2>
                    <p className="text-sm text-muted-foreground mt-1">{g.description}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <h2 className="text-xl font-bold mt-14 mb-2">Subject-by-subject guides</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Every ADC Part 1 subject - high-yield topics, classic exam traps and how to study it.
          </p>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="subject-guides-grid">
            {Object.entries(SUBJECT_GUIDES).map(([s, g]) => (
              <Link key={s} to={`/guides/${s}`} data-testid={`guide-link-${s}`}
                className="block rounded-2xl border border-border bg-card p-5 hover:border-primary/40 transition-colors">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h3 className="font-semibold">{g.subject}</h3>
                  <span className="text-xs font-semibold text-primary bg-primary/10 rounded-full px-2 py-0.5 flex-shrink-0">{g.weight}</span>
                </div>
                <p className="text-sm text-muted-foreground">{g.description}</p>
              </Link>
            ))}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <article className="max-w-3xl mx-auto px-6 py-16" data-testid="guide-article">
        <Link to="/guides" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4" /> All guides
        </Link>
        <h1 className="text-3xl md:text-4xl font-bold mb-8 leading-tight">
          {seoEntry?.h1 || (guide ? guide.title : `ADC Part 1 ${subjectGuide.subject}: High-Yield Topics, Example Questions & Study Tips`)}
        </h1>
        <div className="guide-content text-sm md:text-base">
          {guide ? guide.body : <SubjectGuideArticle guide={subjectGuide} />}
        </div>
        {(guide?.faqs || subjectGuide?.faqs) && (
          <section className="mt-12 border-t border-border pt-8" data-testid="guide-faq-section">
            <h2 className="text-xl md:text-2xl font-bold mb-6">Frequently asked questions</h2>
            <div className="space-y-6">
              {(guide?.faqs || subjectGuide?.faqs).map(([q, a]) => (
                <div key={q}>
                  <h3 className="font-semibold mb-2">{q}</h3>
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </section>
        )}
        <CTA />
        <RelatedGuides slug={slug} />
        <p className="text-xs text-muted-foreground mt-8">
          PassADC is an independent study platform and is not affiliated with, endorsed by, or sponsored by
          the Australian Dental Council. Exam details change - always confirm current format and fees at adc.org.au.
        </p>
      </article>
      <Footer />
    </div>
  );
}
